import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../ui/Button';
import ProgressBar from '../../ui/Progress/ProgressBar';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { finesService, errorMessage } from '../../services/finesService';
import { formatMoney } from '../../constants/finance';
import { confirmDialog } from '../../ui/feedback';
import { useRowLinks } from '../TrafficFines/components/jobLinks';
import { branchNameOf } from '../../services/contractRules';

/**
 * The one-time catch-up of late Salik (owner, 3 Oct 2026: "قبل ما هعمل ستارت فروم ازاى هضيف سالك المتأخر كله مرة واحده لكل
 * العقود"): every contract with Salik / Darb trips not on Ejar since a day - open and closed, each month its own line
 * ("كل شهر سطره"), a closed contract only what its Tolls item and lines miss ("فى قفل العقد ... بنحطهم قيمة اجمالية فى
 * toll") - a preview of all first, then one "write all" (each contract read again on Ejar right before its write).
 * Its parts stand apart - what to start, how far it is (percent: "اعمل مؤشر ... كنسبة مؤية"), what it found - never mixed
 * ("الكلام هنا داخل فى بعضه").
 */
const day = (t) => (t && String(t).length >= 10 ? `${String(t).slice(8, 10)}/${String(t).slice(5, 7)}/${String(t).slice(0, 4)}` : '—');
const POLL_MS = 5000;

const noteOf = (l) => {
    if (l.closed) {
        return `closed: Tolls item ${formatMoney(Number(l.tolls) || 0)} + lines hold ${formatMoney(Number(l.ejarHolds) || 0)} `
            + `of ${formatMoney(Number(l.owed) || 0)} - ${formatMoney(Number(l.short) || 0)} short`;
    }
    const staff = (l.old || []).filter((o) => !o.yelo);
    if (staff.length) return `replaces ${staff.map((o) => `"${o.comment}" ${formatMoney(Number(o.amount) || 0)} by ${o.by || 'staff'}`).join(', ')}`;
    if ((l.old || []).length) return "replaces yelo's line of the month";
    return '';
};

// a long text in a table cell wraps inside its own column (the table keeps cells on one line by default)
const Wrap = ({ children, min = 240, max = 440, mono = false }) => (
    <div className={mono ? 'font-mono' : ''} style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: min, maxWidth: max, margin: '0 auto' }}>
        {children}
    </div>
);

const Part = ({ title, children }) => (
    <section className="rounded-lg border border-gray-200 bg-white p-3 flex flex-col gap-2">
        {title && <h4 className="text-sm font-semibold text-gray-900">{title}</h4>}
        {children}
    </section>
);

// a line is chosen by its contract and month (owner, 5 Oct 2026: "عايز يكون فى اختيارات مش هسجل كله") - the write takes
// only the chosen ones; what a line ADDS on Ejar is its amount less the line it replaces
const keyOf = (l) => `${l.contract}|${l.month}`;
const addsOf = (l) => Math.round(((Number(l.new?.final) || 0) - (Number(l.oldGross) || 0)) * 100) / 100;
const SORTS = {
    contract: (l) => l.contract, month: (l) => l.month, trips: (l) => Number(l.new?.trips) || 0,
    final: (l) => Number(l.new?.final) || 0, adds: addsOf,
};
// the choice is this viewer's until the write (kept in the browser so a reload does not lose it)
const readPick = (id) => {
    try {
        const v = JSON.parse(window.localStorage.getItem(`salikCatchupPick:${id}`) || 'null');
        return v && typeof v === 'object' ? v : null;
    } catch { return null; }
};
const savePick = (id, v) => {
    try { window.localStorage.setItem(`salikCatchupPick:${id}`, JSON.stringify(v)); } catch { /* nothing kept: still works */ }
};
const SELECT = 'border border-gray-300 rounded px-1.5 py-0.5 text-sm bg-white';

const Tile = ({ label, value, tone = 'text-gray-900' }) => (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-center">
        <div className="text-[11px] font-medium text-gray-600">{label}</div>
        <div className={`text-lg font-bold tabular-nums ${tone}`}>{value}</div>
    </div>
);

export default function SalikCatchUp() {
    const { ContractLink, modals } = useRowLinks();   // a contract opens the customer 360 in one click (job-table rule)
    const [from, setFrom] = useState('2026-01-01');
    const [job, setJob] = useState(null);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = useCallback(async () => {
        try {
            const j = await finesService.ejarAutoSalikCatchup();
            setJob(j && j.status ? j : null);
        } catch (e) { setErr(errorMessage(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const running = job?.status === 'running';
    useEffect(() => {
        if (!running) return undefined;
        const t = setInterval(load, POLL_MS);
        return () => clearInterval(t);
    }, [running, load]);

    const rows = useMemo(() => (job?.results || []).flatMap((r) => (r.lines || [])
        .filter((l) => l.new && ['planned', 'written', 'failed'].includes(l.result))
        .map((l, i) => ({ ...l, contract: r.contract, kind: r.kind, branch: branchNameOf(r.contract), key: `${r.contract}-${l.month}-${i}` }))), [job]);
    const unread = (job?.results || []).filter((r) => r.status === 'failed' && !(r.lines || []).length);
    // nothing written for these: Ejar would hold more than the trips are worth, or a closed contract's shortfall no trip fits
    const person = useMemo(() => (job?.results || []).flatMap((r) => (r.lines || []).filter((l) => l.person)
        .map((l, i) => ({ ...l, contract: r.contract, key: `${r.contract}-p-${i}` }))), [job]);
    // a write's totals: what was written (a refused line holds no money on Ejar)
    const counted = job?.write ? rows.filter((l) => l.result === 'written') : rows;
    const contracts = new Set(counted.map((l) => l.contract)).size;
    const total = counted.reduce((a, l) => a + (Number(l.new?.final) || 0), 0);
    const replaced = rows.filter((l) => l.replacesStaff).length;
    const closedN = rows.filter((l) => l.closed).length;
    const canWrite = job?.status === 'done' && !job.write && rows.length > 0;

    // the choice: lines adding less than "minAdd" are left out by themselves (owner: "الفرق بينهم اقل من درهم مش
    // هسجلهم"); a tick on a line wins over that rule
    const pickId = canWrite ? `${job.from}|${job.startedAt || ''}` : null;
    const [minAdd, setMinAdd] = useState(1);
    const [overrides, setOverrides] = useState({});
    useEffect(() => {
        if (!pickId) return;
        const v = readPick(pickId);
        setMinAdd(v && Number.isFinite(Number(v.minAdd)) ? Number(v.minAdd) : 1);
        setOverrides((v && v.overrides) || {});
    }, [pickId]);
    useEffect(() => { if (pickId) savePick(pickId, { minAdd, overrides }); }, [pickId, minAdd, overrides]);
    const isChosen = useCallback((l) => {
        const o = overrides[keyOf(l)];
        return o !== undefined ? o : addsOf(l) >= (Number(minAdd) || 0);
    }, [overrides, minAdd]);
    const setMany = (list, val) => setOverrides((o) => {
        const n = { ...o };
        list.forEach((l) => { n[keyOf(l)] = val; });
        return n;
    });

    // what is shown: filters and the sort (owner: "اوبشن اختيار وتنظيم")
    const [fState, setFState] = useState('all');
    const [fKind, setFKind] = useState('all');
    // a branch alone (owner, 8 Oct 2026: "ينفع اسجل فرع معين مرة واحده") - the branch is in the contract number
    const [fBranch, setFBranch] = useState('all');
    const [fMonth, setFMonth] = useState('all');
    const [fShow, setFShow] = useState('all');
    const [q, setQ] = useState('');
    const [sort, setSort] = useState({ key: 'contract', dir: 1 });
    const kinds = useMemo(() => [...new Set(rows.map((l) => l.kind).filter(Boolean))].sort(), [rows]);
    const months = useMemo(() => [...new Set(rows.map((l) => l.month).filter(Boolean))].sort(), [rows]);
    const branches = useMemo(() => [...new Set(rows.map((l) => l.branch))].sort(), [rows]);
    const shown = useMemo(() => {
        const needle = q.trim().toUpperCase();
        const get = SORTS[sort.key] || SORTS.contract;
        return rows.filter((l) => (fState === 'all' || (fState === 'closed') === !!l.closed)
            && (fKind === 'all' || l.kind === fKind) && (fMonth === 'all' || l.month === fMonth)
            && (fBranch === 'all' || l.branch === fBranch)
            && (!needle || l.contract.includes(needle))
            && (!canWrite || fShow === 'all' || (fShow === 'chosen') === isChosen(l)))
            .sort((a, b) => {
                const x = get(a); const y = get(b);
                return (x < y ? -1 : x > y ? 1 : keyOf(a) < keyOf(b) ? -1 : 1) * sort.dir;
            });
    }, [rows, fState, fKind, fMonth, fBranch, fShow, q, sort, canWrite, isChosen]);
    const chosenRows = canWrite ? rows.filter(isChosen) : rows;
    const chosenContracts = new Set(chosenRows.map((l) => l.contract)).size;
    const chosenAdds = chosenRows.reduce((a, l) => a + addsOf(l), 0);
    const allShownChosen = shown.length > 0 && shown.every(isChosen);
    const sortHead = (k, children) => (
        <TableHeader key={k} aria-sort={sort.key === k ? (sort.dir > 0 ? 'ascending' : 'descending') : 'none'}>
            <button type="button" style={{ color: 'inherit', font: 'inherit' }}
                onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : 1 }))}>
                {children}{sort.key === k ? (sort.dir > 0 ? ' ▲' : ' ▼') : ''}
            </button>
        </TableHeader>
    );

    const state = running ? 'running' : job?.status === 'done' ? 'done' : 'stopped';
    // a run stopped by Stop or by a restart of the engine continues from where it stopped (owner, 4 Oct 2026: "لما اعيدها
    // تانى تبدأ من حيث توقفت") - it runs on the server: the laptop may be closed meanwhile
    const stopped = job?.status === 'stopped';
    const stopRun = async () => {
        setBusy(true); setErr(null);
        try { await finesService.ejarAutoSalikCatchupStop(); await load(); } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
    };
    const resume = async () => {
        setBusy(true); setErr(null);
        try { await finesService.ejarAutoSalikCatchupRun({ from: job.from, write: !!job.write, resume: true }); await load(); }
        catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
    };

    const start = async (write) => {
        const left = rows.length - chosenRows.length;
        if (write && !(await confirmDialog({
            title: 'Write the chosen late Salik on Ejar?',
            message: `${chosenRows.length} chosen line(s) on ${chosenContracts} contract(s), ${formatMoney(chosenAdds)} added on Ejar (with VAT).`
                + (left ? `\n${left} line(s) left out - nothing is written for them, and their trips go on no other line.` : '')
                + '\n\nEach contract is read again on Ejar right before its line is written; a closed contract only gets what its Tolls item and lines miss.',
            confirmLabel: `Write ${chosenRows.length}`,
            danger: true,
        }))) return;
        setBusy(true); setErr(null);
        try {
            await finesService.ejarAutoSalikCatchupRun(write ? { from: job.from, write, pick: chosenRows.map(keyOf) } : { from, write });
            await load();
        } catch (e) { setErr(errorMessage(e)); } finally { setBusy(false); }
    };

    return (
        <div className="flex flex-col gap-3 border-t-2 border-gray-200 pt-3">
            <Part>
                <div className="flex flex-col gap-0.5">
                    <span className="text-base font-semibold text-gray-900">Catch up late Salik</span>
                    <span className="text-xs text-gray-600">every contract with trips not on Ejar, open and closed · each month its own line ·
                        a closed contract only what its Tolls item and lines miss · a preview first, nothing written until "Write all"</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                    <label htmlFor="salik-catchup-from" className="text-gray-700">Trips from</label>
                    <input id="salik-catchup-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                        className="border border-gray-300 rounded px-2 py-0.5 font-mono text-sm" />
                    <Button size="xs" disabled={busy || running || !from} onClick={() => start(false)}>{stopped ? 'Start over' : 'Preview all'}</Button>
                    {running && <Button size="xs" variant="secondary" disabled={busy || !!job?.stop} onClick={stopRun}>{job?.stop ? 'Stopping…' : 'Stop'}</Button>}
                    {stopped && <Button size="xs" variant="primary" disabled={busy} onClick={resume}>Continue from {job.done} of {job.total}</Button>}
                </div>
                <span className="text-xs text-gray-500">It runs on the server: you may close the page or the laptop. Stop keeps what it did; Continue goes on from there.</span>
                {err && <div role="alert" className="text-sm font-medium text-red-700">{err}</div>}
            </Part>

            {job && (
                <Part title={job.write ? 'Writing on Ejar' : 'Preview - reading every contract on Ejar (nothing is written)'}>
                    <ProgressBar done={job.done} total={job.total} unit="contracts" state={state}
                        label={`trips from ${day(job.from)} up to ${day(job.till)}`}
                        note={[
                            job.by ? `started by ${job.by}${job.startedAt ? ` at ${String(job.startedAt).slice(11, 16)}` : ''}` : '',
                            running ? `${job.lines || 0} line(s), ${formatMoney(Number(job.amount) || 0)} found so far` : '',
                            job.status === 'failed' ? `stopped: ${job.error}` : '',
                            stopped ? `stopped by ${job.stoppedBy || 'an admin'} - Continue goes on from contract ${(job.done || 0) + 1}` : '',
                            job.invygo ? `${job.invygo} of them Invygo` : '',
                        ].filter(Boolean).join(' · ')} />
                </Part>
            )}

            {job && !running && (job.results || []).length > 0 && (
                <Part title={job.write ? 'What was written' : 'What the preview found'}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2">
                        <Tile label={job.write ? 'Lines written' : 'Lines to write'} tone="text-purple-800"
                            value={job.write ? rows.filter((l) => l.result === 'written').length : rows.length} />
                        <Tile label="Contracts" value={contracts} />
                        <Tile label="With VAT" value={formatMoney(total)} />
                        <Tile label="Replace a staff line" value={replaced} tone={replaced ? 'text-amber-800' : 'text-gray-900'} />
                        <Tile label="Closed (Tolls short)" value={closedN} />
                        <Tile label="For a person" value={person.length} tone={person.length ? 'text-amber-800' : 'text-gray-900'} />
                        <Tile label={job.write ? 'Refused by Ejar' : 'Not read'} tone="text-red-700"
                            value={job.write ? rows.filter((l) => l.result === 'failed').length : unread.length} />
                    </div>
                    {canWrite && (
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                                <label htmlFor="salik-catchup-min" className="text-gray-700">Leave out lines that add less than AED</label>
                                <input id="salik-catchup-min" type="number" min="0" step="0.5" value={minAdd}
                                    onChange={(e) => setMinAdd(e.target.value === '' ? 0 : Number(e.target.value))}
                                    className="w-20 border border-gray-300 rounded px-2 py-0.5 text-sm tabular-nums" />
                                <span className="text-xs text-gray-500">a tick on a line below wins over this</span>
                            </div>
                            <div className="text-sm font-medium text-gray-800" data-testid="salik-catchup-chosen">
                                Chosen {chosenRows.length} of {rows.length} lines · {chosenContracts} contract(s) ·
                                {' '}{formatMoney(chosenAdds)} added on Ejar
                                {rows.length - chosenRows.length > 0 && <span className="text-amber-800"> · {rows.length - chosenRows.length} left out</span>}
                            </div>
                            <div>
                                <Button variant="danger" disabled={busy || !chosenRows.length} onClick={() => start(true)}>
                                    Write {chosenRows.length} chosen lines on Ejar
                                </Button>
                            </div>
                        </div>
                    )}
                </Part>
            )}

            {job?.status === 'done' && !job.write && !rows.length && (
                <Part><div className="text-sm font-medium text-green-700">Nothing late to write.</div></Part>
            )}

            {!running && rows.length > 0 && (
                <Part title={job.write ? `Lines (${rows.length})` : `Lines to write (${rows.length})`}>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                        <input type="search" aria-label="Find a contract" placeholder="Contract…" value={q}
                            onChange={(e) => setQ(e.target.value)} className="border border-gray-300 rounded px-2 py-0.5 text-sm font-mono w-44" />
                        <select aria-label="State" value={fState} onChange={(e) => setFState(e.target.value)} className={SELECT}>
                            <option value="all">Open and closed</option><option value="open">Open</option><option value="closed">Closed</option>
                        </select>
                        <select aria-label="Branch" value={fBranch} onChange={(e) => setFBranch(e.target.value)} className={SELECT}>
                            <option value="all">Every branch</option>
                            {branches.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                        <select aria-label="Kind" value={fKind} onChange={(e) => setFKind(e.target.value)} className={SELECT}>
                            <option value="all">Every kind</option>
                            {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
                        </select>
                        <select aria-label="Month" value={fMonth} onChange={(e) => setFMonth(e.target.value)} className={SELECT}>
                            <option value="all">Every month</option>
                            {months.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                        {canWrite && (
                            <select aria-label="Show" value={fShow} onChange={(e) => setFShow(e.target.value)} className={SELECT}>
                                <option value="all">Chosen and left out</option><option value="chosen">Chosen</option><option value="left">Left out</option>
                            </select>
                        )}
                        <span className="text-xs text-gray-600">{shown.length} shown</span>
                        {canWrite && (
                            <span className="flex flex-wrap gap-1">
                                <Button size="xs" variant="secondary" disabled={!shown.length} onClick={() => setMany(shown, true)}>Choose all shown</Button>
                                {/* only what the filters show is written - a branch, a kind, a month alone */}
                                <Button size="xs" variant="secondary" disabled={!shown.length}
                                    title="Every line shown is chosen, every other line left out (the AED rule above no longer applies - Reset ticks brings it back)"
                                    onClick={() => { const on = new Set(shown.map(keyOf)); setOverrides(Object.fromEntries(rows.map((l) => [keyOf(l), on.has(keyOf(l))]))); }}>
                                    Choose only the shown</Button>
                                <Button size="xs" variant="secondary" disabled={!shown.length} onClick={() => setMany(shown, false)}>Leave out all shown</Button>
                                <Button size="xs" variant="secondary" disabled={!Object.keys(overrides).length} onClick={() => setOverrides({})}>Reset ticks</Button>
                            </span>
                        )}
                    </div>
                    <TableContainer>
                        <Table sticky>
                            <TableHead><TableRow>
                                {canWrite && (
                                    <TableHeader>
                                        <input type="checkbox" aria-label="Choose every line shown" checked={allShownChosen}
                                            onChange={() => setMany(shown, !allShownChosen)} />
                                    </TableHeader>
                                )}
                                <TableHeader>#</TableHeader>
                                {sortHead('contract', 'Contract')}<TableHeader>State</TableHeader>{sortHead('month', 'Month')}
                                <TableHeader>Line on Ejar</TableHeader>{sortHead('trips', 'Trips')}{sortHead('final', 'With VAT')}
                                {sortHead('adds', 'Adds on Ejar')}
                                <TableHeader>Note</TableHeader>{job.write && <TableHeader>Result</TableHeader>}
                            </TableRow></TableHead>
                            <TableBody>
                                {shown.map((l, n) => (
                                    <TableRow key={l.key} style={canWrite && !isChosen(l) ? { opacity: 0.5 } : undefined}>
                                        {canWrite && (
                                            <TableCell>
                                                <input type="checkbox" aria-label={`Choose ${l.contract} ${l.month}`} checked={isChosen(l)}
                                                    onChange={(e) => setMany([l], e.target.checked)} />
                                            </TableCell>
                                        )}
                                        <TableCell>{n + 1}</TableCell>
                                        <TableCell><ContractLink contract={l.contract} /></TableCell>
                                        <TableCell>{l.closed ? 'closed' : 'open'} · {l.kind}<div className="text-xs text-gray-500">{l.branch}</div></TableCell>
                                        <TableCell className="font-mono">{l.month}</TableCell>
                                        <TableCell><Wrap mono min={300} max={460}>{l.new.comment}</Wrap></TableCell>
                                        <TableCell>{l.new.difference ? 'difference' : l.new.trips}</TableCell>
                                        <TableCell>{formatMoney(Number(l.new.final) || 0)}</TableCell>
                                        <TableCell className={addsOf(l) < 1 ? 'text-amber-800 font-semibold' : ''}>{formatMoney(addsOf(l))}</TableCell>
                                        <TableCell><Wrap min={160} max={320}><span className="text-xs">{noteOf(l)}</span></Wrap></TableCell>
                                        {job.write && <TableCell>{l.result === 'failed' ? <Wrap min={160}><span className="text-red-700">{l.error}</span></Wrap> : l.result}</TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Part>
            )}

            {!running && person.length > 0 && (
                <Part title={`For a person - nothing written (${person.length})`}>
                    <TableContainer>
                        <Table sticky>
                            <TableHead><TableRow><TableHeader>#</TableHeader><TableHeader>Contract</TableHeader><TableHeader>Month</TableHeader><TableHeader>Why</TableHeader></TableRow></TableHead>
                            <TableBody>
                                {person.map((l, n) => (
                                    <TableRow key={l.key}>
                                        <TableCell>{n + 1}</TableCell>
                                        <TableCell><ContractLink contract={l.contract} /></TableCell>
                                        <TableCell className="font-mono">{l.month}</TableCell>
                                        <TableCell><Wrap min={320} max={620}><span className="text-xs">{l.skip}</span></Wrap></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Part>
            )}

            {!running && unread.length > 0 && (
                <Part title={`Could not be read (${unread.length})`}>
                    <div className="text-xs text-red-700">
                        {unread.slice(0, 10).map((r) => `${r.contract} (${r.error})`).join('; ')}{unread.length > 10 ? ' …' : ''}
                    </div>
                </Part>
            )}
            {modals}
        </div>
    );
}
